import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  get,
} from "react-hook-form";
import React, {
  type ChangeEvent,
  type FocusEvent,
  forwardRef,
  type ReactElement,
  type Ref,
} from "react";
import { InputV2, type InputV2Props } from "src/components/InputV2/InputV2.tsx";
import { composeRefs } from "@exonn/hooks";
import { css } from "@emotion/react";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { Flex } from "src/components/Flex.tsx";

export type TextFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
  forceValue?: any;
  readOnly?: boolean;
  labelWidth?: number;
} & InputV2Props &
  Omit<ControllerProps<TFieldValues, TName>, "render">;

export const TextFieldImpl = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: TextFieldProps<TFieldValues, TName>,
  ref: Ref<HTMLInputElement>,
) => {
  const {
    name,
    control,
    disabled,
    forceValue,
    labelWidth,
    defaultValue,
    placeholder,
    rules,
    readOnly,
    shouldUnregister,
    onChange: onChangeProp,
    onBlur: onBlurProp,
    ...restProps
  } = props;

  return (
    <Controller
      key={name}
      name={name}
      control={control}
      rules={rules}
      shouldUnregister={shouldUnregister}
      // disabled={disabled}
      defaultValue={defaultValue}
      render={({
        field: { name, value, onChange, onBlur, ref: innerRef },
        formState: { errors },
      }) => {
        const resolvedValue = forceValue != null ? forceValue : value;

        if (readOnly) {
          return (
            <Flex>
              <span
                css={labelStyle({
                  width: labelWidth,
                })}
              >
                {props.label}:
              </span>
              <span css={valueStyle}>{resolvedValue}</span>
            </Flex>
          );
        }
        const commonProps = {
          autoComplete: "off",
        };

        const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
          onChangeProp?.(event);
          onChange(event.currentTarget.value);
        };

        const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
          onBlurProp?.(event);
          onBlur();
        };

        return (
          <InputV2
            {...commonProps}
            value={resolvedValue}
            name={name}
            placeholder={placeholder}
            disabled={disabled}
            onChange={handleChange}
            onBlur={handleBlur}
            error={
              get(errors?.root || {}, name)?.message // server error
            }
            {...restProps}
            ref={composeRefs(innerRef, ref)}
          />
        );
      }}
    />
  );
};

export const TextField = forwardRef(TextFieldImpl) as <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: TextFieldProps<TFieldValues, TName> & {
    ref?: Ref<HTMLInputElement>;
  },
) => ReactElement;

const labelStyle =
  ({ width }: { width?: number }) =>
  (theme: ExactTheme) => css`
    font-size: ${theme.fontSizesV2.body};
    font-weight: ${theme.fontWeights.normal};
    color: ${theme.colorsV2.grey[800]};
    margin-right: 5px;
    display: flex;
    width: ${width != null ? width + "px" : "unset"};
  `;

const valueStyle = (theme: ExactTheme) => css`
  font-size: ${theme.fontSizesV2.body};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colorsV2.grey[900]};
`;
